/**
 * Serviço de Avaliações
 *
 * Gerencia operações CRUD de avaliações e resultados usando PostgREST.
 * Tabelas: avaliacao, avaliacao_resultado
 */

import { postgrestClient } from "../lib/postgrest/client";
import { registrarFalha } from "../lib/telemetria";

/**
 * Serviço de avaliações
 */
export const avaliacoesService = {
  /**
   * Lista avaliações com filtros
   * @param {Object} options
   * @param {Object} options.filters - Filtros (ex: { turma_id: "eq.123", tipo: "eq.ENTRADA" })
   * @param {string} options.order - Ordenação (ex: "data_aplicacao.desc")
   * @param {number} options.limit - Limite de resultados
   * @param {number} options.offset - Offset para paginação
   * @returns {Promise<Array>}
   */
  async list(options = {}) {
    const { filters = {}, order = "data_aplicacao.desc", limit, offset } = options;
    return postgrestClient.get("avaliacao", {
      filters,
      order,
      limit,
      offset,
    });
  },

  /**
   * Busca avaliação por ID
   * @param {string} id
   * @returns {Promise<Object>}
   */
  async getById(id) {
    const result = await postgrestClient.get("avaliacao", {
      filters: { id: `eq.${id}` },
    });
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Cria nova avaliação
   * @param {Object} avaliacaoData
   * @returns {Promise<Object>}
   */
  async create(avaliacaoData) {
    // Transforma campos do frontend para o formato do banco
    const payload = {
      turma_id: avaliacaoData.turmaId || avaliacaoData.turma_id,
      tipo: avaliacaoData.tipo,
      disciplina: avaliacaoData.disciplina || null,
      atividade_esportiva_id: avaliacaoData.atividadeEsportivaId || avaliacaoData.atividade_esportiva_id || null,
      titulo: avaliacaoData.titulo,
      descricao: avaliacaoData.descricao || null,
      data_aplicacao: avaliacaoData.dataAplicacao || avaliacaoData.data_aplicacao,
      gabarito: avaliacaoData.gabarito || [],
      criado_por: avaliacaoData.criadoPor || avaliacaoData.criado_por || null,
    };

    const result = await postgrestClient.post("avaliacao", payload);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Cria nova avaliação esportiva
   * @param {Object} avaliacaoData
   * @returns {Promise<Object>}
   */
  async createEsportiva(avaliacaoData) {
    const payload = {
      turma_id: avaliacaoData.turmaId || avaliacaoData.turma_id,
      tipo: avaliacaoData.tipo,
      disciplina: null, // NULL para avaliações esportivas
      atividade_esportiva_id: avaliacaoData.atividadeEsportivaId || avaliacaoData.atividade_esportiva_id,
      titulo: avaliacaoData.titulo,
      descricao: avaliacaoData.descricao || null,
      data_aplicacao: avaliacaoData.dataAplicacao || avaliacaoData.data_aplicacao,
      gabarito: [], // Array vazio para avaliações esportivas
      criado_por: avaliacaoData.criadoPor || avaliacaoData.criado_por || null,
    };

    const result = await postgrestClient.post("avaliacao", payload);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Atualiza avaliação
   * @param {string} id
   * @param {Object} avaliacaoData
   * @returns {Promise<Object>}
   */
  async update(id, avaliacaoData) {
    const payload = {};
    if (avaliacaoData.turmaId || avaliacaoData.turma_id) {
      payload.turma_id = avaliacaoData.turmaId || avaliacaoData.turma_id;
    }
    if (avaliacaoData.tipo) payload.tipo = avaliacaoData.tipo;
    if (avaliacaoData.disciplina) payload.disciplina = avaliacaoData.disciplina;
    if (avaliacaoData.titulo) payload.titulo = avaliacaoData.titulo;
    if (avaliacaoData.descricao !== undefined) payload.descricao = avaliacaoData.descricao;
    if (avaliacaoData.dataAplicacao || avaliacaoData.data_aplicacao) {
      payload.data_aplicacao = avaliacaoData.dataAplicacao || avaliacaoData.data_aplicacao;
    }
    if (avaliacaoData.gabarito) payload.gabarito = avaliacaoData.gabarito;
    if (avaliacaoData.atualizadoPor || avaliacaoData.atualizado_por) {
      payload.atualizado_por = avaliacaoData.atualizadoPor || avaliacaoData.atualizado_por;
    }

    const result = await postgrestClient.patch("avaliacao", { id }, payload);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Remove avaliação
   * @param {string} id
   * @returns {Promise<null>}
   */
  async delete(id) {
    return postgrestClient.delete("avaliacao", { id });
  },

  /**
   * Busca avaliações de uma turma
   * @param {string} turmaId
   * @param {Object} options - { disciplina, tipo, atividadeEsportivaId }
   * @returns {Promise<Array>}
   */
  async getByTurma(turmaId, options = {}) {
    const filters = { turma_id: `eq.${turmaId}` };
    if (options.disciplina) filters.disciplina = `eq.${options.disciplina}`;
    if (options.tipo) filters.tipo = `eq.${options.tipo}`;
    if (options.atividadeEsportivaId) {
      filters.atividade_esportiva_id = `eq.${options.atividadeEsportivaId}`;
    }

    const todas = await this.list({ filters });

    // Se buscar por atividade esportiva, filtra manualmente (PostgREST pode não suportar NULL checks)
    if (options.atividadeEsportivaId) {
      return todas.filter(av => av.atividade_esportiva_id === options.atividadeEsportivaId);
    }

    return todas;
  },

  /**
   * Cria resultado de avaliação
   * @param {Object} resultadoData
   * @returns {Promise<Object>}
   */
  async createResultado(resultadoData) {
    const payload = {
      avaliacao_id: resultadoData.avaliacaoId || resultadoData.avaliacao_id,
      aluno_id: resultadoData.alunoId || resultadoData.aluno_id,
      respostas: resultadoData.respostas || null,
      acertos: resultadoData.acertos || null,
      erros: resultadoData.erros || null,
      score: resultadoData.score || null,
      nivel: resultadoData.nivel || null,
      performance: resultadoData.performance || null,
      comportamento: resultadoData.comportamento || null,
      lancado_por: resultadoData.lancadoPor || resultadoData.lancado_por || null,
    };

    const result = await postgrestClient.post("avaliacao_resultado", payload);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Cria múltiplos resultados (lote)
   * @param {string} avaliacaoId
   * @param {Array<Object>} resultados
   * @returns {Promise<Array>}
   */
  async createResultadosLote(avaliacaoId, resultados) {
    const payloads = resultados.map(resultado => ({
      avaliacao_id: avaliacaoId,
      aluno_id: resultado.alunoId || resultado.aluno_id,
      respostas: (Array.isArray(resultado.respostas) && resultado.respostas.length > 0) ? resultado.respostas : null,
      acertos: resultado.acertos || null,
      erros: resultado.erros || null,
      score: resultado.score || null,
      nivel: resultado.nivel || null,
      performance: resultado.performance || null,
      comportamento: resultado.comportamento || null,
      lancado_por: resultado.lancadoPor || resultado.lancado_por || null,
    }));

    // PostgREST não suporta bulk insert nativamente, então fazemos um por um
    // Ou podemos usar uma RPC function se disponível
    const promises = payloads.map(payload => postgrestClient.post("avaliacao_resultado", payload));
    const results = await Promise.all(promises);
    return results.map(r => (Array.isArray(r) ? r[0] : r));
  },

  /**
   * Cria múltiplos resultados esportivos (lote)
   * @param {string} avaliacaoId
   * @param {Array<Object>} resultados - Array de { alunoId, performance, comportamento }
   * @returns {Promise<Array>}
   */
  async createResultadosEsportivosLote(avaliacaoId, resultados) {
    const payloads = resultados.map(resultado => ({
      avaliacao_id: avaliacaoId,
      aluno_id: resultado.alunoId || resultado.aluno_id,
      respostas: null,
      acertos: null,
      erros: null,
      score: null,
      nivel: null,
      performance: resultado.performance,
      comportamento: resultado.comportamento,
      lancado_por: resultado.lancadoPor || resultado.lancado_por || null,
    }));

    try {
      const promises = payloads.map(payload => postgrestClient.post("avaliacao_resultado", payload));
      const results = await Promise.all(promises);
      return results.map(r => (Array.isArray(r) ? r[0] : r));
    } catch (err) {
      const status = err?.status || err?.statusCode || err?.response?.status;
      if (status === 403 || status === 401) {
        throw new Error(
          "Você não tem permissão para salvar as notas desta avaliação. Verifique seu vínculo com o núcleo da turma."
        );
      }
      throw err;
    }
  },

  /**
   * Busca resultados de uma avaliação
   * @param {string} avaliacaoId
   * @returns {Promise<Array>}
   */
  async getResultados(avaliacaoId) {
    return postgrestClient.get("avaliacao_resultado", {
      filters: { avaliacao_id: `eq.${avaliacaoId}` },
      select: "*,alunos(*),avaliacao(*)",
    });
  },

  /**
   * Busca resultado de um aluno em uma avaliação
   * @param {string} avaliacaoId
   * @param {string} alunoId
   * @returns {Promise<Object>}
   */
  async getResultado(avaliacaoId, alunoId) {
    const result = await postgrestClient.get("avaliacao_resultado", {
      filters: {
        avaliacao_id: `eq.${avaliacaoId}`,
        aluno_id: `eq.${alunoId}`,
      },
      select: "*,alunos(*),avaliacao(*)",
    });
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Atualiza resultado de avaliação
   * @param {string} id
   * @param {Object} resultadoData
   * @returns {Promise<Object>}
   */
  async updateResultado(id, resultadoData) {
    const payload = {};
    if (resultadoData.respostas !== undefined) payload.respostas = resultadoData.respostas;
    if (resultadoData.acertos !== undefined) payload.acertos = resultadoData.acertos;
    if (resultadoData.erros !== undefined) payload.erros = resultadoData.erros;
    if (resultadoData.score !== undefined) payload.score = resultadoData.score;
    if (resultadoData.nivel !== undefined) payload.nivel = resultadoData.nivel;
    if (resultadoData.performance !== undefined) payload.performance = resultadoData.performance;
    if (resultadoData.comportamento !== undefined) payload.comportamento = resultadoData.comportamento;

    const result = await postgrestClient.patch("avaliacao_resultado", { id }, payload);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Busca evolução de proficiência de um aluno
   * Pode usar uma RPC function ou fazer join manual
   * @param {string} alunoId
   * @param {Object} options - { disciplina, anoLetivo, useHistoricoFunction }
   * @returns {Promise<Array>}
   */
  async getEvolucao(alunoId, options = {}) {
    const { disciplina, useHistoricoFunction } = options;

    // Se for histórico do aluno, usar função RPC que permite acesso completo
    if (useHistoricoFunction) {
      try {
        // Passar parâmetros sempre, mesmo que null
        // PostgREST pode ter problemas quando parâmetros são omitidos
        const rpcParams = {
          p_aluno_id: alunoId,
          p_disciplina: disciplina || null,
        };



        const resultado = await postgrestClient.rpc("buscar_avaliacoes_historico_aluno", rpcParams);

        // Mapear resultado para formato esperado
        return Array.isArray(resultado) ? resultado.map(res => ({
          id: res.resultado_id,
          avaliacao_id: res.avaliacao_id,
          aluno_id: res.aluno_id,
          alunoId: res.aluno_id,
          score: Number(res.score) || 0,
          nivel: res.nivel || 1,
          acertos: res.acertos || 0,
          erros: res.erros || 0,
          totalQuestoes: res.total_questoes || 0,
          total_questoes: res.total_questoes || 0,
          performance: res.performance || null,
          comportamento: res.comportamento || null,
          lancado_em: res.lancado_em,
          avaliacao: {
            id: res.avaliacao_id,
            tipo: res.tipo,
            disciplina: res.disciplina,
            atividade_esportiva_id: res.atividade_esportiva_id || null,
            titulo: res.titulo,
            data_aplicacao: res.data_aplicacao,
            dataAplicacao: res.data_aplicacao,
            criado_em: res.criado_em || res.avaliacao_criado_em || null,
            created_at: res.created_at || res.avaliacao_created_at || null,
            gabarito: [], // Não incluído na função RPC
          },
          turma: {
            turma_id: res.turma_id,
            id: res.turma_id,
            nome: res.turma_nome,
          },
          turmaNome: res.turma_nome,
          turmaId: res.turma_id,
          data: res.data_aplicacao,
        })) : [];
      } catch (error) {
        registrarFalha("avaliacoes.historico_rpc", error);
        // Se useHistoricoFunction é true mas a RPC falhou, fazer fallback para método normal
        // Isso permite que os dados sejam exibidos mesmo se o PostgREST não reconhecer a função
        if (useHistoricoFunction) {
          console.warn("[avaliacoes.service] RPC falhou, usando método normal como fallback (pode ter limitações de RLS)");
          // Continuar para o método normal abaixo
        } else {
          // Se não for histórico, retornar array vazio em caso de erro
          return [];
        }
      }
    }

    const filters = { aluno_id: `eq.${alunoId}` };

    // Se houver RPC function, usar ela
    // Caso contrário, fazer join manual
    // Nota: Não podemos ordenar por campos de relações no PostgREST diretamente
    // Então fazemos a ordenação no cliente após receber os dados
    const resultados = await postgrestClient.get("avaliacao_resultado", {
      filters,
      select: "*,avaliacao(*,turma(nome,turma_id))",
      // Removido order aqui pois PostgREST não suporta ordenação por campos de relações
      // Nota: atividade_esportiva_id está em avaliacao, mas não há foreign key, então não podemos fazer join
    });

    // Filtra por disciplina se especificado
    let filtered = Array.isArray(resultados) ? resultados : [];
    if (options.disciplina) {
      filtered = filtered.filter(r => r.avaliacao?.disciplina === options.disciplina);
    }

    // Ordena por data_aplicacao da avaliação (crescente) no cliente
    filtered.sort((a, b) => {
      const dataA = a.avaliacao?.data_aplicacao || a.avaliacao?.dataAplicacao || "";
      const dataB = b.avaliacao?.data_aplicacao || b.avaliacao?.dataAplicacao || "";

      if (!dataA && !dataB) return 0;
      if (!dataA) return 1;
      if (!dataB) return -1;

      // Compara datas (formato ISO string)
      return new Date(dataA).getTime() - new Date(dataB).getTime();
    });

    return filtered;
  },
};

export default avaliacoesService;

